import { useState, useCallback } from 'react';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { AccordionItem } from 'foundations-components/transitional/components/AccordionItem';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import PropertyInputV2 from 'customer-data-properties/input/PropertyInputV2';
import { NOT_SPECIFIED } from 'customer-data-objects/property/AccessLevel';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { H2 } from 'foundations-components/transitional/components/H2';
import { H5 } from 'foundations-components/transitional/components/H5';
import { propertyLabelTranslator,
// @ts-expect-error type not available yet
propertyDescriptionTranslator } from 'property-translator/propertyTranslator';
import { FAILED, STARTED } from 'conversations-async-data/async-data/constants/asyncStatuses';
import { getPropertyValueRequirementsFromStage } from 'svh-tickets-lib/tickets/public/operators';
import { useGetAdditionalPropertyValue } from '../hooks/useGetAdditionalPropertyValue';
import emptyFunction from 'react-utils/emptyFunction';
import I18n from 'I18n';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const alwaysUndefined = emptyFunction.thatReturns(undefined);
const getIsPropertyEmpty = propertyValue => ['', null, undefined].includes(propertyValue);
const validationSettings = {
  validateInitialValue: true
};
const RequiredFieldsModal = ({
  onConfirm,
  onReject,
  objectLabel = '',
  objectId,
  objectTypeId,
  propertyValues,
  stageChangeStatus,
  subject,
  toStage
}) => {
  const [draft, setDraft] = useState({});
  const [draftValidation, setDraftValidation] = useState({});
  const getPropertyValue = useCallback(name => Object.prototype.hasOwnProperty.call(draft, name) ? draft[name] : propertyValues[name] && propertyValues[name].value, [draft, propertyValues]);
  const handleChange = useCallback(changes => {
    setDraft(currentDraft => Object.assign({}, currentDraft, changes));
  }, [setDraft]);
  const handleValidationChange = useCallback(validation => {
    const {
      propertyName
    } = validation;
    setDraftValidation(prevDraftValidation => Object.assign({}, prevDraftValidation, {
      [propertyName]: validation
    }));
  }, [setDraftValidation]);
  const handleConfirm = useCallback(() => {
    const saveReadyProperties = Object.keys(draft).map(name => ({
      name,
      value: draft[name]
    }));
    onConfirm(saveReadyProperties);
  }, [draft, onConfirm]);
  const getSavedValue = useGetAdditionalPropertyValue(subject);
  const renderPropertyInput = ({
    propertyName,
    isValueRequired
  }) => {
    const property = propertyValues[propertyName];
    const propertyDefinition = property.definition;
    const isHubspotDefined = propertyDefinition.hubspotDefined;
    const propertyLabel = isHubspotDefined ? propertyLabelTranslator(propertyDefinition.label) : propertyDefinition.label;
    const propertyDescription = isHubspotDefined ? propertyDescriptionTranslator(propertyDefinition.label, propertyDefinition.description) : propertyDefinition.description;
    const formControlLabel = /*#__PURE__*/_jsx(Tooltip, {
      title: propertyDescription,
      children: /*#__PURE__*/_jsx("span", {
        children: propertyLabel
      })
    });
    const canEditProperty = property.permission === undefined // not all portal have FLP access so we don't always get this value
    ? true : property.permission.accessLevel === NOT_SPECIFIED;
    const formControlProps = {
      required: isValueRequired,
      label: formControlLabel,
      labelTooltip: !canEditProperty ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "svh-workspace-ui-lib.ticket-header.requiredValuesModal.readOnlyProperty"
      }) : undefined
    };
    return /*#__PURE__*/_jsx(PropertyInputV2, {
      value: getPropertyValue(propertyName),
      formControlProps: formControlProps,
      onChange: handleChange,
      onValidationChange: handleValidationChange,
      property: propertyDefinition,
      objectId: `${objectId}`,
      objectTypeId: objectTypeId,
      readOnly: !canEditProperty,
      validationSettings: validationSettings,
      getDraftValue: alwaysUndefined,
      getSavedValue: getSavedValue,
      inputProps: {
        'data-selenium-test': `property-input-${propertyName}`
      }
    }, propertyName);
  };
  const stagePropertyRequirements = getPropertyValueRequirementsFromStage(toStage) || [];
  const propertiesWithoutValues = stagePropertyRequirements.filter(({
    propertyName
  }) => getIsPropertyEmpty(propertyValues[propertyName] && propertyValues[propertyName].value));
  const propertiesWithExistingValues = stagePropertyRequirements.filter(({
    propertyName
  }) => !getIsPropertyEmpty(propertyValues[propertyName] && propertyValues[propertyName].value));
  const previouslyCompletedPropertiesSection = propertiesWithExistingValues.length ? /*#__PURE__*/_jsx(AccordionItem, {
    title: /*#__PURE__*/_jsx(H5, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "svh-workspace-ui-lib.ticket-header.requiredValuesModal.previouslyCompletedProperties"
      })
    }),
    defaultOpen: !propertiesWithoutValues.length,
    paddingBlockStart: "600",
    paddingBlockEnd: "400",
    children: propertiesWithExistingValues.map(renderPropertyInput)
  }) : null;
  const header = /*#__PURE__*/_jsxs(DialogHeader, {
    children: [/*#__PURE__*/_jsx(H2, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "svh-workspace-ui-lib.ticket-header.requiredValuesModal.header"
      })
    }), /*#__PURE__*/_jsx(DialogCloseButton, {
      onClick: onReject
    })]
  });
  const body = /*#__PURE__*/_jsxs(DialogBody, {
    children: [/*#__PURE__*/_jsx(View, {
      paddingBlockEnd: "800",
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "svh-workspace-ui-lib.ticket-header.requiredValuesModal.bodyMessage",
        options: {
          count: stagePropertyRequirements ? stagePropertyRequirements.length : 0,
          stageLabel: propertyLabelTranslator(toStage.label),
          objectLabel: objectLabel || I18n.text('svh-workspace-ui-lib.ticket-header.requiredValuesModal.bodyMessageDefaultObject')
        }
      })
    }), propertiesWithoutValues.map(renderPropertyInput), previouslyCompletedPropertiesSection]
  });
  const isValueMissingForRequiredProperty = !!stagePropertyRequirements.find(propertyRequirement => propertyRequirement.isValueRequired && getIsPropertyEmpty(getPropertyValue(propertyRequirement.propertyName)));
  const areAllPropertiesSaveable = Object.values(draftValidation).every(({
    saveable
  }) => !!saveable);
  const areAnyValidationsLoading = Object.values(draftValidation).some(({
    loading
  }) => !!loading);
  const saveButtonDisabled = isValueMissingForRequiredProperty || !areAllPropertiesSaveable;
  const footer = /*#__PURE__*/_jsxs(DialogFooter, {
    children: [/*#__PURE__*/_jsx(Button, {
      "data-selenium-test": "required-fields-modal-save-button",
      use: "primary",
      onClick: handleConfirm,
      disabled: saveButtonDisabled || areAnyValidationsLoading || stageChangeStatus === STARTED || stageChangeStatus === FAILED,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "svh-workspace-ui-lib.ticket-header.requiredValuesModal.buttons.confirm"
      })
    }), /*#__PURE__*/_jsx(Button, {
      onClick: onReject,
      "data-selenium-test": "required-fields-modal-cancel-button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "svh-workspace-ui-lib.ticket-header.requiredValuesModal.buttons.cancel"
      })
    })]
  });
  return /*#__PURE__*/_jsxs(Modal, {
    children: [header, body, footer]
  });
};
RequiredFieldsModal.displayName = 'RequiredFieldsModal';
export default RequiredFieldsModal;